// QueueV2Display — the live, white, hospital-facing queue board.
//
// Resolves what to show (normally a dashboard-configured TV profile, or a
// manual /v2/setup selection), seeds from the v2 /day snapshot, and stays live
// over SSE with no polling. Renders either the Column or Slideshow layout.
// Redirects to the profile picker if nothing is configured.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { Logo } from "../../assets/icons/Icons";
import { useUserData } from "../../lib/contexts/UserContext";
import { useHospDocData } from "../../lib/contexts/HospitalDoctorContext";
import { Doctor } from "../../lib/utils/types";
import { useQueueStream } from "../hooks/useQueueStream";
import { useTVScale } from "../hooks/useTVScale";
import { debugOverlayEnabled } from "../utils/debugFlags";
import DebugOverlay from "../components/DebugOverlay";
import {
  DEFAULT_SELECTION,
  QueueV2Selection,
  clearProfileId,
  loadProfileId,
  loadScreenPrefs,
  loadSelection,
} from "../utils/selection";
import { TVProfile, fetchProfile } from "../api/profiles";
import { setAudioLanguageToLocalStorage } from "../../lib/utils/speechUtils";
import { TVConfigProvider } from "../contexts/TVConfigContext";
import RotatingBoard from "../components/RotatingBoard";
import QueueAnnouncer from "../components/QueueAnnouncer";
import AdsPanel from "../components/AdsPanel";
import FullscreenButton from "../components/FullscreenButton";
import IdleScreen from "../components/IdleScreen";

const StatusPill: React.FC<{
  status: ReturnType<typeof useQueueStream>["status"];
}> = ({ status }) => {
  const map: Record<string, { label: string; color: string }> = {
    connecting: { label: "Connecting", color: "#D99A06" },
    open: { label: "Live", color: "#15A05B" },
    reconnecting: { label: "Reconnecting", color: "#D99A06" },
    error: { label: "Disconnected", color: "#C53030" },
  };
  const { label, color } = map[status] ?? map.error;
  return (
    <div className="flex items-center gap-2">
      <span
        className="inline-block h-2.5 w-2.5 rounded-full"
        style={{ backgroundColor: color }}
      />
      <span className="text-sm font-medium" style={{ color }}>
        {label}
      </span>
    </div>
  );
};

// A panel plus the same bottom row RotatingBoard reserves for its page
// controls, holding the fullscreen toggle. Used for the ad column beside the
// board and for the full-width ad / idle panel, so the ad ends at the same
// height in every state and the fullscreen button stays reachable on a TV
// whose queue is empty.
const PanelWithControls: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div className="flex min-h-0 min-w-0 flex-1 flex-col">
    <div className="min-h-0 flex-1">{children}</div>
    <div className="mt-3 flex h-6 shrink-0 items-center justify-center">
      <FullscreenButton />
    </div>
  </div>
);

const QueueV2Display: React.FC = () => {
  const { hospital_id } = useParams();
  const navigate = useNavigate();
  const { accessToken, setAccessToken } = useUserData();
  const { allDoctorsData } = useHospDocData();

  // Scale the 1080p design to the TV's actual viewport (see the hook).
  useTVScale();

  const [selection, setSelection] = useState<QueueV2Selection | null>(null);
  const [profile, setProfile] = useState<TVProfile | null>(null);
  const [resolved, setResolved] = useState(false);
  const [now, setNow] = useState(() => moment().format("hh:mm A"));

  // The displayed day. A TV runs 24/7, so we can't freeze "today" at mount —
  // we roll it at local midnight. Changing this state re-runs useQueueStream's
  // effect (date is in its deps), which refetches the /day snapshot and
  // reopens the SSE stream scoped to the new day, the same as on first load.
  // The TV is always pinned to today (no date picker), so there's no
  // "following a chosen past date" case to skip.
  const [date, setDate] = useState(() => moment().format("YYYY-MM-DD"));

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const rollIfNeeded = () => {
      const today = moment().format("YYYY-MM-DD");
      setDate((prev) => (prev === today ? prev : today));
    };
    const schedule = () => {
      // +1s cushion so moment() has definitely ticked over to the new date when
      // the timer fires (firing exactly at 00:00:00.000 risks reading yesterday
      // due to timer jitter).
      const ms = moment().add(1, "day").startOf("day").diff(moment()) + 1000;
      timer = setTimeout(() => {
        rollIfNeeded();
        schedule(); // re-arm for the following midnight
      }, ms);
    };
    schedule();
    // Backgrounded tabs throttle long timers, so a tab left across midnight may
    // not fire on time — re-check the date whenever the document refocuses.
    const onVisible = () => {
      if (document.visibilityState === "visible") rollIfNeeded();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  // Resolve what to display. A profile id is re-read from the server every
  // time, so an edit made in the dashboard reaches the screen on its next load
  // — no one has to touch the TV. Falls back to a manual /v2/setup selection.
  const resolveDisplay = useCallback(async () => {
    // Same reason as the picker screen: on a page refresh the interceptor has
    // no token yet, and fetching now would 401 and bounce a correctly-configured
    // TV back to the picker. The effect below re-runs once the token lands.
    if (!hospital_id || !accessToken) return;

    const profileId = loadProfileId(hospital_id);
    if (profileId) {
      try {
        const p = await fetchProfile(profileId);
        // Deleted or deactivated in the dashboard — send the TV back to pick
        // again rather than showing a board nobody can explain.
        if (!p || !p.isActive) {
          clearProfileId(hospital_id);
          navigate(`/${hospital_id}/v2`, { replace: true });
          return;
        }
        setProfile(p);
        setSelection(null);
        setAudioLanguageToLocalStorage(p.language || "en");
        setResolved(true);
        return;
      } catch (err) {
        // A network blip must not black out a running waiting-room screen.
        // Keep whatever is already on it; the next reload retries.
        console.warn("[queue-v2] profile refresh failed:", err);
        if (profile) return;
      }
    }

    const sel = loadSelection(hospital_id);
    if (!sel || sel.mappingIds.length === 0) {
      navigate(`/${hospital_id}/v2`, { replace: true });
      return;
    }
    setSelection(sel);
    setProfile(null);
    // Re-applied on every load for the same reason the profile path does it:
    // audioLanguage is a single browser-wide key, so a profile picked earlier
    // on this TV could otherwise leave the manual setup speaking the wrong one.
    setAudioLanguageToLocalStorage(sel.language);
    setResolved(true);
  }, [hospital_id, navigate, profile, accessToken]);

  // Re-resolve on mount, once the access token lands, and whenever the day rolls
  // over — the midnight roll is a free daily refresh, so dashboard edits land
  // without polling for them.
  useEffect(() => {
    resolveDisplay();
    // resolveDisplay is intentionally omitted: it depends on `profile`, which
    // it also sets, and would otherwise re-run on every refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hospital_id, date, accessToken]);

  // Live clock in the header.
  useEffect(() => {
    const id = setInterval(() => setNow(moment().format("hh:mm A")), 1000 * 30);
    return () => clearInterval(id);
  }, []);

  // Doctors this hospital shows on a TV at all. Resolving the configured ids
  // against this list is also what applies hide_from_tv (the profiles API
  // doesn't filter it) and what gives us pictures and specialisations.
  const visibleDoctors = useMemo<Doctor[]>(
    () => (allDoctorsData ?? []).filter((d) => d && !d.hide_from_tv),
    [allDoctorsData]
  );

  // Resolve to full doctor records, preserving the configured order. A default
  // profile can't hold doctors — the backend rejects mappings on it — so it
  // means "everyone" rather than "no one".
  const doctors = useMemo<Doctor[]>(() => {
    const byId = new Map(visibleDoctors.map((d) => [d.mapping_id, d]));
    if (profile) {
      if (profile.isDefault || profile.doctors.length === 0) return visibleDoctors;
      return profile.doctors
        .map((d) => byId.get(d.mapping_id))
        .filter((d): d is Doctor => Boolean(d));
    }
    if (!selection) return [];
    return selection.mappingIds
      .map((id) => byId.get(id))
      .filter((d): d is Doctor => Boolean(d));
  }, [profile, selection, visibleDoctors]);

  // Taken from the config rather than from `doctors` so the stream can open
  // before the doctor list has finished loading.
  const mappingIds = useMemo(() => {
    if (profile) {
      if (profile.isDefault || profile.doctors.length === 0) {
        return visibleDoctors.map((d) => d.mapping_id);
      }
      return profile.doctors.map((d) => d.mapping_id);
    }
    return selection?.mappingIds ?? [];
  }, [profile, selection, visibleDoctors]);

  const viewMode =
    profile?.viewMode ?? selection?.viewMode ?? DEFAULT_SELECTION.viewMode;

  // Cadence and the ad panel: a manual selection carries its own, a
  // profile-driven screen uses this screen's saved prefs (set on the picker).
  const screenPrefs = useMemo(
    () => (hospital_id ? loadScreenPrefs(hospital_id) : null),
    // Re-read alongside the profile so a change on the picker takes effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hospital_id, profile]
  );
  const intervalSec =
    selection?.intervalSec ?? screenPrefs?.intervalSec ?? DEFAULT_SELECTION.intervalSec;
  const ads = selection?.ads ?? screenPrefs?.ads ?? DEFAULT_SELECTION.ads;

  const {
    bookingsByMapping,
    delaysByAvailability,
    sessionsByAvailability,
    status,
    lastError,
    seeded,
  } = useQueueStream({
    hospitalId: hospital_id,
    token: accessToken,
    mappingIds,
    date,
    onToken: setAccessToken,
  });

  // Only doctors with someone waiting get a card — an empty "No patients in
  // queue" panel is dead space on a waiting-room TV. Recomputed on every
  // booking_event, so a card appears the moment that doctor's first patient
  // checks in and drops out again once their queue clears. Fewer cards also
  // means fewer rotation pages, so the board self-compacts.
  const activeDoctors = useMemo(
    () =>
      doctors.filter((d) => (bookingsByMapping[d.mapping_id]?.length ?? 0) > 0),
    [doctors, bookingsByMapping]
  );

  // Nobody waiting anywhere → hand the whole screen to ads (or the idle panel)
  // rather than showing a board of nothing. Gated on `seeded` because
  // bookingsByMapping is empty until the first /day snapshot lands, and without
  // it every mount would flash the idle screen before the queue appeared.
  const idle = seeded && doctors.length > 0 && activeDoctors.length === 0;

  if (!resolved) return null;

  return (
    <TVConfigProvider config={profile?.fields}>
    {/* h-dvh (dynamic viewport) on top of h-screen: on a TV browser whose
        address bar hasn't collapsed, 100vh is taller than the visible area and
        the board spilled off the bottom. overflow-hidden guarantees the page
        itself never scrolls — only the patient lists do. */}
    <div className="flex h-screen h-dvh w-full flex-col overflow-hidden bg-[#F7F9FC]">
      {/* Voice announcements (renders only the audio-permission modal) */}
      <QueueAnnouncer doctors={doctors} bookingsByMapping={bookingsByMapping} />

      {/* ?debug=1 — on-screen diagnostics for a TV without devtools */}
      {debugOverlayEnabled() && <DebugOverlay />}

      {/* Header */}
      <div className="flex shrink-0 items-center justify-between border-b border-[#E9EDF3] bg-white px-8 py-3">
        <div className="flex items-center gap-4">
          {/* The SVG hard-codes 150x37px; rem so it scales with the board. */}
          <div className="[&>svg]:h-[2.3125rem] [&>svg]:w-[9.375rem]">
            <Logo />
          </div>
          <div className="border-l border-[#E9EDF3] pl-4">
            <h1 className="text-lg font-semibold text-[#1F2B4D]">Live Queue</h1>
            <p className="text-xs text-[#9AA5B8]">
              {moment(date).format("dddd, DD MMM YYYY")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <span className="text-lg font-semibold tabular-nums text-[#1F2B4D]">
            {now}
          </span>
          <StatusPill status={status} />
          <button
            onClick={() => navigate(`/${hospital_id}/v2`)}
            className="rounded-full border border-[#E3E8F0] px-4 py-1.5 text-sm text-[#5A6478] hover:bg-[#F2F5FA]"
          >
            Change view
          </button>
        </div>
      </div>

      {lastError && (
        <div className="shrink-0 border-b border-[#F6D9D9] bg-[#FDF1F1] px-8 py-2.5 text-sm text-[#B23B3B]">
          {lastError}
        </div>
      )}

      {/* Board (+ optional right-side ad panel), or the idle screen */}
      <div className="flex min-h-0 flex-1 gap-6 p-6">
        {idle ? (
          // Empty queue: ads take the full area when enabled, and fall back to
          // the idle panel if the hospital hasn't uploaded any. Same bottom
          // row as beside the queue, so the ad sits at one height in both.
          <PanelWithControls>
            {ads ? (
              <AdsPanel
                hospitalId={hospital_id}
                fallback={<IdleScreen now={now} date={date} />}
                fit="contain"
              />
            ) : (
              <IdleScreen now={now} date={date} />
            )}
          </PanelWithControls>
        ) : (
          <>
            <div className="min-h-0 min-w-0 flex-1">
              {doctors.length === 0 ? (
                <div className="flex h-full items-center justify-center text-[#9AA5B8]">
                  Selected doctors are no longer available — please reconfigure.
                </div>
              ) : (
                // Slideshow → 1 doctor per screen; Column → up to 2, rotating.
                // Only doctors with patients are passed in.
                <RotatingBoard
                  doctors={activeDoctors}
                  bookingsByMapping={bookingsByMapping}
                  delaysByAvailability={delaysByAvailability}
                  sessionsByAvailability={sessionsByAvailability}
                  pageSize={viewMode === "slideshow" ? 1 : 2}
                  variant={viewMode === "slideshow" ? "slide" : "column"}
                  intervalSec={intervalSec}
                  fillWidth={ads}
                />
              )}
            </div>

            {/* flex-1 on both halves (not a % width + max-width) so the split
                stays 50/50 at any resolution. A fixed cap here would hand the
                board an ever-larger share as the screen grows — at 1080p a
                640px cap works out to a 65/35 split.
                No width breakpoint here (v1 has none either): a TV browser
                running zoomed can report a CSS width under 768px, and a
                display:none video keeps playing its audio over a blank
                panel. */}
            {/* RotatingBoard reserves the same bottom row whenever ads are
                up, so the ad ends level with the doctor panels. */}
            {ads && (
              <PanelWithControls>
                <AdsPanel hospitalId={hospital_id} />
              </PanelWithControls>
            )}
          </>
        )}
      </div>
    </div>
    </TVConfigProvider>
  );
};

export default QueueV2Display;
